import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Chip, Paper, MenuItem, IconButton, InputAdornment, Stack, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import {
  Add, Delete, Edit, Preview, ChevronRight, Search, Lock, ContentCopy, OpenInNew,
  ArrowUpward, ArrowDownward, Close, Check, InfoOutlined, VisibilityOutlined,
} from '@mui/icons-material';
import {
  getReportSchema, getReportDefinitions, createReportDefinition, updateReportDefinition,
  deleteReportDefinition, previewReport,
  ReportDefinition, ReportColumn, DataSourceSchema, ReportRunResult,
} from '../../services/reportService';
import { formatMoney } from '../../utils/currency';

const MODULE_LABELS: Record<string, string> = {
  transportation: 'Transportation',
  food: 'Food Delivery',
  grocery: 'Grocery',
  porter: 'Porter',
  on_demand: 'Home Services',
  events: 'Events',
  analytics: 'Analytics',
  wallet: 'Wallet',
  subscriptions: 'Subscriptions',
};

const NAME_MAX = 80;
const SLUG_MAX = 60;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function moduleLabel(module?: string) {
  if (!module) return '—';
  return MODULE_LABELS[module] || module.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function slugify(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, SLUG_MAX).replace(/-$/, '');
}

function apiMessage(err: unknown, fallback: string) {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied' : 'Copy slug'}>
      <IconButton
        size="small"
        aria-label="Copy slug"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          });
        }}
      >
        <ContentCopy sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

function SystemCustomInfo() {
  return (
    <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
      <strong>System</strong> reports ship with the platform. Anyone can open and run them, but they are
      read-only and cannot be edited or deleted. <strong>Custom</strong> reports are created by your team in the
      Report Builder and can be edited or deleted.
    </Alert>
  );
}

function RunReportsTab() {
  const navigate = useNavigate();
  const { data: definitions, isLoading } = useQuery({ queryKey: ['report-definitions'], queryFn: getReportDefinitions });

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open a report to filter, search, and export on its own page.
      </Typography>
      <Grid container spacing={2}>
        {(definitions || []).map((r) => (
          <Grid item xs={12} sm={6} md={4} key={r._id}>
            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                cursor: 'pointer',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2.5,
                height: '100%',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
              }}
              onClick={() => navigate(`/reports/${r.slug}`)}
            >
              <Box display="flex" justifyContent="space-between" alignItems="start" gap={1}>
                <Tooltip title={r.name.length > 32 ? r.name : ''}>
                  <Typography fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {r.name}
                  </Typography>
                </Tooltip>
                <Chip
                  label={r.isSystem ? 'System' : 'Custom'}
                  size="small"
                  icon={r.isSystem ? <Lock sx={{ fontSize: 14 }} /> : undefined}
                  color={r.isSystem ? 'default' : 'primary'}
                  variant={r.isSystem ? 'filled' : 'outlined'}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                {moduleLabel(r.module)} · {r.slug}
              </Typography>
              <Button size="small" endIcon={<ChevronRight />} sx={{ mt: 1.25, px: 0, textTransform: 'none', fontWeight: 700 }}>
                Open report
              </Button>
            </Paper>
          </Grid>
        ))}
        {!isLoading && !(definitions || []).length && (
          <Grid item xs={12}>
            <Typography color="text.secondary">No reports yet. Create one in Report Builder.</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

type DialogMode = 'create' | 'edit' | 'view';

interface FormState {
  name: string;
  slug: string;
  slugEdited: boolean;
  dataSource: string;
  reportType: 'list' | 'aggregate';
  groupBy: string;
  columns: ReportColumn[];
}

const EMPTY_FORM: FormState = {
  name: '', slug: '', slugEdited: false, dataSource: '', reportType: 'list', groupBy: '', columns: [],
};

function PreviewTable({ result }: { result: ReportRunResult }) {
  const rows = (result.rows || []).slice(0, 5);
  const cell = (value: unknown, type?: string) => {
    if (value == null || value === '') return '—';
    if (type === 'currency') return formatMoney(value);
    if (type === 'date') {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('en-US');
    }
    return String(value);
  };
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={800} gutterBottom>
        Preview — {result.meta.total.toLocaleString()} matching record{result.meta.total === 1 ? '' : 's'}
        {rows.length ? ` (first ${rows.length} shown)` : ''}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No records match yet. The report will still save.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {result.columns.map((c) => <TableCell key={c.key} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{c.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={String(r._id ?? i)}>
                  {result.columns.map((c) => (
                    <TableCell key={c.key} sx={{ whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {cell(r[c.key], c.type)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

function ReportFormDialog({
  open, mode, report, schema, definitions, onClose,
}: {
  open: boolean;
  mode: DialogMode;
  report: ReportDefinition | null;
  schema: DataSourceSchema[];
  definitions: ReportDefinition[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const readOnly = mode === 'view';
  const [form, setForm] = useState<FormState>(() => {
    if (!report) return EMPTY_FORM;
    const q = (report.query || {}) as Record<string, unknown>;
    return {
      name: report.name,
      slug: report.slug,
      slugEdited: true,
      dataSource: String(q.dataSource || ''),
      reportType: q.type === 'aggregate' ? 'aggregate' : 'list',
      groupBy: String(q.groupBy || ''),
      columns: report.columns || [],
    };
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [previewAttempted, setPreviewAttempted] = useState(false);

  const source = schema.find((s) => s.key === form.dataSource);
  const effectiveSlug = form.slugEdited ? form.slug.trim() : slugify(form.name);
  const isList = form.reportType === 'list';

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    const name = form.name.trim();
    if (!name) e.name = 'Name is required';
    else if (name.length < 3) e.name = 'Name must be at least 3 characters';
    else if (name.length > NAME_MAX) e.name = `Name must be ${NAME_MAX} characters or fewer`;
    else if (definitions.some((d) => d._id !== report?._id && d.name.trim().toLowerCase() === name.toLowerCase())) {
      e.name = 'Another report already uses this name';
    }

    if (mode === 'create') {
      if (!effectiveSlug) e.slug = 'Slug is required — enter a name or type a slug';
      else if (effectiveSlug.length > SLUG_MAX || !SLUG_PATTERN.test(effectiveSlug)) {
        e.slug = 'Use lowercase letters, numbers and single hyphens only (e.g. daily-rides)';
      } else {
        const clash = definitions.find((d) => d.slug === effectiveSlug);
        if (clash) e.slug = `Slug already used by “${clash.name}”`;
      }
    }

    if (!form.dataSource) e.dataSource = 'Select a data source';
    if (isList && !form.columns.length) e.columns = 'Select at least one column';
    if (!isList && !form.groupBy) e.groupBy = 'Select the field to group by';
    return e;
  }, [form, effectiveSlug, definitions, report, mode, isList]);

  const previewBlockers = [errors.dataSource, isList ? errors.columns : errors.groupBy].filter(Boolean) as string[];
  const showError = (key: string) => (submitAttempted || touched[key]) && !!errors[key];

  const buildPayload = () => ({
    name: form.name.trim(),
    slug: mode === 'create' ? effectiveSlug : undefined,
    module: source?.module,
    query: {
      dataSource: form.dataSource,
      type: form.reportType,
      groupBy: isList ? undefined : form.groupBy,
      metrics: isList ? undefined : [{ field: '_id', op: 'count', alias: 'Records' }],
      limit: 500,
      sort: { field: 'createdAt', direction: 'desc' },
    },
    columns: isList ? form.columns : [],
    filters: [
      { key: 'status', label: 'Status', field: 'status', type: 'select', operator: 'eq' },
      { key: 'dateFrom', label: 'From', field: 'createdAt', type: 'date', operator: 'gte' },
      { key: 'dateTo', label: 'To', field: 'createdAt', type: 'date', operator: 'lte' },
    ].filter((f) => f.key !== 'status' || (source?.filterOptions?.status || []).length > 0),
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = buildPayload();
      return mode === 'edit' && report ? updateReportDefinition(report._id, payload) : createReportDefinition(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-definitions'] });
      onClose();
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => {
      const p = buildPayload();
      return previewReport({ name: p.name || 'Preview', query: p.query, columns: p.columns, filters: [] });
    },
  });

  const patch = (next: Partial<FormState>) => setForm((prev) => ({ ...prev, ...next }));

  const toggleColumn = (field: DataSourceSchema['fields'][0]) => {
    setTouched((t) => ({ ...t, columns: true }));
    if (form.columns.some((c) => c.field === field.key)) {
      patch({ columns: form.columns.filter((c) => c.field !== field.key) });
      return;
    }
    patch({
      columns: [...form.columns, { key: field.key.replace(/\./g, '_'), label: field.label, field: field.key, type: field.type }],
    });
  };

  const moveColumn = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= form.columns.length) return;
    const next = [...form.columns];
    [next[index], next[target]] = [next[target], next[index]];
    patch({ columns: next });
  };

  const save = () => {
    setSubmitAttempted(true);
    if (Object.keys(errors).length) return;
    saveMutation.mutate();
  };

  const runPreview = () => {
    setPreviewAttempted(true);
    if (previewBlockers.length) return;
    previewMutation.mutate();
  };

  const title = mode === 'create' ? 'Create Report' : mode === 'edit' ? 'Edit Report' : 'Report Configuration';
  const groupByFields = (source?.fields || []).filter((f) => f.type !== 'date' && f.type !== 'currency' && f.type !== 'number');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {title}
        {readOnly && <Chip size="small" icon={<Lock sx={{ fontSize: 14 }} />} label="System · Read-only" />}
      </DialogTitle>
      <DialogContent dividers>
        {readOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This is a built-in system report. You can view its configuration and open it to run with filters,
            but it cannot be edited or deleted. To change it, create a custom report with the same data source.
          </Alert>
        )}
        {submitAttempted && Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700}>Fix these before saving:</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {Object.values(errors).map((msg) => <li key={msg}><Typography variant="body2">{msg}</Typography></li>)}
            </Box>
          </Alert>
        )}
        {saveMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>{apiMessage(saveMutation.error, 'Could not save the report.')}</Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth required label="Name" value={form.name} disabled={readOnly}
              onChange={(e) => patch({ name: e.target.value.slice(0, NAME_MAX) })}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              error={showError('name')}
              helperText={showError('name') ? errors.name : `${form.name.trim().length}/${NAME_MAX} · shown in the report list`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Slug"
              value={mode === 'create' ? effectiveSlug : form.slug}
              disabled={mode !== 'create'}
              onChange={(e) => {
                const value = e.target.value.toLowerCase().slice(0, SLUG_MAX);
                patch({ slug: value, slugEdited: value.length > 0 });
              }}
              onBlur={() => setTouched((t) => ({ ...t, slug: true }))}
              error={mode === 'create' && showError('slug')}
              helperText={mode !== 'create'
                ? 'The slug is the report URL and cannot change after creation'
                : showError('slug')
                  ? errors.slug
                  : form.slugEdited
                    ? 'Custom slug — must be unique. Clear it to auto-generate from Name again.'
                    : 'Auto-generated from Name (used in the report URL). Edit to customise.'}
              InputProps={{
                endAdornment: mode === 'create' && form.slugEdited ? (
                  <InputAdornment position="end">
                    <Tooltip title="Reset to auto-generated">
                      <IconButton size="small" onClick={() => patch({ slug: '', slugEdited: false })}><Close fontSize="small" /></IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select fullWidth required label="Data Source" value={form.dataSource} disabled={readOnly || mode === 'edit'}
              onChange={(e) => patch({ dataSource: e.target.value, columns: [], groupBy: '' })}
              onBlur={() => setTouched((t) => ({ ...t, dataSource: true }))}
              error={showError('dataSource')}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              helperText={showError('dataSource')
                ? errors.dataSource
                : mode === 'edit'
                  ? 'Data source is fixed after creation'
                  : 'The records this report reads. The available columns below depend on it; changing it clears selected columns.'}
            >
              <MenuItem value="" disabled>Select a data source</MenuItem>
              {schema.map((s) => <MenuItem key={s.key} value={s.key}>{s.label} · {moduleLabel(s.module)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select fullWidth label="Report Type" value={form.reportType} disabled={readOnly}
              onChange={(e) => patch({ reportType: e.target.value as FormState['reportType'] })}
              helperText={isList
                ? 'List: one row per record (e.g. each trip) with the columns you choose.'
                : 'Aggregate: groups records by one field and counts them (e.g. trips per status).'}
            >
              <MenuItem value="list">List — one row per record</MenuItem>
              <MenuItem value="aggregate">Aggregate — count per group</MenuItem>
            </TextField>
          </Grid>

          {!isList && (
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth required label="Group by" value={form.groupBy} disabled={readOnly || !source}
                onChange={(e) => patch({ groupBy: e.target.value })}
                onBlur={() => setTouched((t) => ({ ...t, groupBy: true }))}
                error={showError('groupBy')}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                helperText={showError('groupBy') ? errors.groupBy : source ? `One row per distinct value of this ${source.label} field` : 'Select a data source first'}
              >
                <MenuItem value="" disabled>Select a field</MenuItem>
                {groupByFields.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
              </TextField>
            </Grid>
          )}

          {isList && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={800}>
                Columns <Typography component="span" color="error.main">*</Typography>
              </Typography>
              {!source ? (
                <Typography variant="body2" color="text.secondary">Select a data source to see its available columns.</Typography>
              ) : (
                <>
                  {!readOnly && (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Fields available in <strong>{source.label}</strong>. Click to add; click a selected (filled) field again to remove it.
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                        {source.fields.map((f) => {
                          const isSelected = form.columns.some((c) => c.field === f.key);
                          return (
                            <Chip
                              key={f.key}
                              label={f.label}
                              size="small"
                              icon={isSelected ? <Check sx={{ fontSize: 16 }} /> : <Add sx={{ fontSize: 16 }} />}
                              color={isSelected ? 'primary' : 'default'}
                              variant={isSelected ? 'filled' : 'outlined'}
                              onClick={() => toggleColumn(f)}
                            />
                          );
                        })}
                      </Box>
                    </>
                  )}
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.75 }}>
                    Selected columns ({form.columns.length}) — shown in this order in the report
                  </Typography>
                  {form.columns.length === 0 ? (
                    <Alert severity={showError('columns') ? 'error' : 'warning'} sx={{ py: 0 }}>
                      {readOnly ? 'Columns for this report are generated by the system.' : 'Select at least one column. Reports need one or more columns to display.'}
                    </Alert>
                  ) : (
                    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                      {form.columns.map((c, i) => (
                        <Stack
                          key={c.field}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ px: 1.5, py: 0.5, borderTop: i ? 1 : 0, borderColor: 'divider' }}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ width: 24 }}>{i + 1}.</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{c.label}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{c.field}</Typography>
                          {!readOnly && (
                            <>
                              <Tooltip title="Move up">
                                <span>
                                  <IconButton size="small" disabled={i === 0} onClick={() => moveColumn(i, -1)}><ArrowUpward fontSize="small" /></IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move down">
                                <span>
                                  <IconButton size="small" disabled={i === form.columns.length - 1} onClick={() => moveColumn(i, 1)}><ArrowDownward fontSize="small" /></IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Remove column">
                                <IconButton size="small" onClick={() => patch({ columns: form.columns.filter((x) => x.field !== c.field) })}><Close fontSize="small" /></IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      ))}
                    </Paper>
                  )}
                </>
              )}
            </Grid>
          )}

          {!readOnly && previewAttempted && previewBlockers.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">To preview, first: {previewBlockers.join('; ').toLowerCase()}.</Alert>
            </Grid>
          )}
          {previewMutation.isError && (
            <Grid item xs={12}>
              <Alert severity="error">{apiMessage(previewMutation.error, 'Preview failed.')}</Alert>
            </Grid>
          )}
          {previewMutation.data && (
            <Grid item xs={12}>
              <PreviewTable result={previewMutation.data} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {readOnly ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" startIcon={<OpenInNew />} onClick={() => report && navigate(`/reports/${report.slug}`)}>
              Open report
            </Button>
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
              <Typography component="span" color="error.main">*</Typography> Required
            </Typography>
            <Button onClick={onClose}>Cancel</Button>
            <Tooltip title={previewBlockers.length ? `Needs: ${previewBlockers.join(', ')}` : 'Run the report with the current settings'}>
              <span>
                <Button startIcon={<Preview />} onClick={runPreview} disabled={previewMutation.isPending}>
                  {previewMutation.isPending ? 'Loading…' : 'Preview'}
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" onClick={save} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create report'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function BuilderTab() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ mode: DialogMode; report: ReportDefinition | null; key: number } | null>(null);
  const [toDelete, setToDelete] = useState<ReportDefinition | null>(null);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const { data: schema } = useQuery({ queryKey: ['report-schema'], queryFn: getReportSchema });
  const { data: definitions, isLoading } = useQuery({ queryKey: ['report-definitions'], queryFn: getReportDefinitions });
  const sourceLabel = (r: ReportDefinition) => {
    const key = String((r.query as Record<string, unknown>)?.dataSource || '');
    return schema?.find((s) => s.key === key)?.label || key || '—';
  };

  const deleteMutation = useMutation({
    mutationFn: deleteReportDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-definitions'] });
      setToDelete(null);
    },
  });

  const modules = useMemo(
    () => [...new Set((definitions || []).map((d) => d.module).filter(Boolean))].sort((a, b) => moduleLabel(a).localeCompare(moduleLabel(b))),
    [definitions],
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (definitions || []).filter((d) => {
      if (moduleFilter && d.module !== moduleFilter) return false;
      if (typeFilter === 'system' && !d.isSystem) return false;
      if (typeFilter === 'custom' && d.isSystem) return false;
      if (!term) return true;
      return `${d.name} ${d.slug} ${moduleLabel(d.module)}`.toLowerCase().includes(term);
    });
  }, [definitions, search, moduleFilter, typeFilter]);

  const openDialog = (mode: DialogMode, report: ReportDefinition | null) => setDialog({ mode, report, key: Date.now() });

  const defColumns: GridColDef<ReportDefinition>[] = [
    {
      field: 'name', headerName: 'Report', flex: 1.4, minWidth: 220,
      renderCell: (p) => (
        <Tooltip title={`Open “${p.row.name}”`}>
          <Button
            size="small"
            endIcon={<OpenInNew sx={{ fontSize: '14px !important' }} />}
            onClick={() => navigate(`/reports/${p.row.slug}`)}
            sx={{ textTransform: 'none', fontWeight: 700, px: 0, minWidth: 0, maxWidth: '100%', justifyContent: 'flex-start' }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.row.name}</Box>
          </Button>
        </Tooltip>
      ),
    },
    {
      field: 'slug', headerName: 'Slug', flex: 1, minWidth: 200,
      renderCell: (p) => (
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0, width: '100%', height: '100%' }}>
          <Tooltip title={p.row.slug}>
            <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>{p.row.slug}</Typography>
          </Tooltip>
          <CopyButton value={p.row.slug} />
        </Stack>
      ),
    },
    {
      field: 'module', headerName: 'Module', width: 160,
      valueGetter: (_v, row) => moduleLabel(row.module),
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }}>
          <Tooltip title={`${p.value} · data source: ${sourceLabel(p.row)}`}>
            <Typography variant="body2" noWrap>{String(p.value)}</Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'isSystem', headerName: 'Type', width: 180,
      valueGetter: (_v, row) => (row.isSystem ? 'System · Read-only' : 'Custom'),
      renderCell: (p) => (
        <Tooltip
          arrow
          title={(
            <Box>
              <Typography variant="caption" display="block" fontWeight={800}>{String(p.value)}</Typography>
              <Typography variant="caption" display="block">
                {p.row.isSystem
                  ? 'Built-in report. You can open and run it, but it cannot be edited or deleted.'
                  : 'Created by your team. It can be edited or deleted.'}
              </Typography>
            </Box>
          )}
        >
          <Chip
            size="small"
            icon={p.row.isSystem ? <Lock sx={{ fontSize: 14 }} /> : undefined}
            label={String(p.value)}
            color={p.row.isSystem ? 'default' : 'primary'}
            variant={p.row.isSystem ? 'filled' : 'outlined'}
            sx={{ maxWidth: '100%' }}
          />
        </Tooltip>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', width: 170, sortable: false, filterable: false,
      renderCell: (p) => {
        const locked = !!p.row.isSystem;
        return (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Open report">
              <IconButton size="small" color="primary" onClick={() => navigate(`/reports/${p.row.slug}`)}><OpenInNew fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="View configuration">
              <IconButton size="small" onClick={() => openDialog('view', p.row)}><VisibilityOutlined fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title={locked ? 'System reports are read-only' : 'Edit'}>
              <span>
                <IconButton size="small" disabled={locked} onClick={() => openDialog('edit', p.row)}><Edit fontSize="small" /></IconButton>
              </span>
            </Tooltip>
            <Tooltip title={locked ? 'System reports cannot be deleted' : 'Delete'}>
              <span>
                <IconButton size="small" color="error" disabled={locked} onClick={() => setToDelete(p.row)}><Delete fontSize="small" /></IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  const hasFilters = !!(search || moduleFilter || typeFilter);

  return (
    <Box>
      <SystemCustomInfo />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} mb={2}>
        <TextField
          size="small"
          placeholder="Search name, slug or module"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment> }}
          sx={{ flex: 1, maxWidth: 380 }}
        />
        <TextField
          select size="small" label="Module" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}
          InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All modules</MenuItem>
          {modules.map((m) => <MenuItem key={m} value={m}>{moduleLabel(m)}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All types</MenuItem>
          <MenuItem value="system">System (read-only)</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </TextField>
        {hasFilters && (
          <Button size="small" onClick={() => { setSearch(''); setModuleFilter(''); setTypeFilter(''); }}>Clear</Button>
        )}
        <Box flex={1} />
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog('create', null)} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          New Report
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {rows.length} of {(definitions || []).length} reports
      </Typography>
      <SerialNoDataGrid
        rows={rows}
        columns={defColumns}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        sx={{ bgcolor: 'background.paper', borderRadius: 2, mb: 2 }}
      />

      {dialog && (
        <ReportFormDialog
          key={dialog.key}
          open
          mode={dialog.mode}
          report={dialog.report}
          schema={schema || []}
          definitions={definitions || []}
          onClose={() => setDialog(null)}
        />
      )}

      <ConfirmDialog
        open={!!toDelete}
        title="Delete report?"
        message={toDelete ? `“${toDelete.name}” (${toDelete.slug}) will be permanently deleted. Anyone using its link will no longer be able to open it.` : ''}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete._id)}
        onCancel={() => setToDelete(null)}
      />
      {deleteMutation.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>{apiMessage(deleteMutation.error, 'Could not delete the report.')}</Alert>
      )}
    </Box>
  );
}

export default function ReportBuilderPage() {
  const [tab, setTab] = useState(0);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance & Reports"
        title="Reports"
        subtitle="Choose a report to open it on a dedicated page with filters, search, and export."
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Reports" />
        <Tab label="Report Builder" />
      </Tabs>
      {tab === 0 && <RunReportsTab />}
      {tab === 1 && <BuilderTab />}
    </PageShell>
  );
}
